import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import hashSum from 'hash-sum'

import {
  createSmartSegment,
  fetchSmartSegment,
  getUsesList,
  renameSmartSegment,
  saveSmartSegmentFilter,
  updateSmartSegmentDescription,
} from 'common/smartSegments/actions/currentSmartSegmentActions'
import { SmartSegment } from 'shared/api/common/schemas/smartSegments'

export interface CurrentSmartSegment {
  item: SmartSegment | null
  isLoading: boolean
  isLoaded: boolean
  hash: string | null
  hasChanges: boolean
}

export const initialState: CurrentSmartSegment = {
  item: null,
  isLoading: false,
  isLoaded: false,
  hash: null,
  hasChanges: false,
}

export const currentSegmentSlice = createSlice({
  name: 'smartSegmentCurrent',
  initialState: initialState,
  reducers: {
    update(state, action: PayloadAction<{ data: Partial<SmartSegment>; replace?: boolean }>) {
      if (state.item == null) {
        return
      }

      const { data, replace = false } = action.payload

      if (replace) {
        state.item = { ...data } as SmartSegment
      } else {
        state.item = { ...state.item, ...data }
      }

      state.hasChanges = state.hash !== hashSum(state.item)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSmartSegment.pending.type, (state) => {
      state.isLoading = true
      state.isLoaded = false
      state.hasChanges = false
    })

    builder.addCase(fetchSmartSegment.rejected.type, (state) => {
      state.isLoading = false
    })

    builder.addCase(
      fetchSmartSegment.fulfilled.type,
      (state, action: PayloadAction<SmartSegment>) => {
        const { payload: item } = action

        state.item = item
        state.isLoading = false
        state.isLoaded = true
        state.hash = hashSum(item)
        state.hasChanges = false
      },
    )

    builder.addCase(createSmartSegment.pending.type, (state) => {
      state.isLoading = true
      state.isLoaded = false
      state.hasChanges = false
    })

    builder.addCase(createSmartSegment.rejected.type, (state) => {
      state.isLoading = false
    })

    builder.addCase(
      createSmartSegment.fulfilled.type,
      (state, action: PayloadAction<SmartSegment>) => {
        const { payload: item } = action
        state.item = item
        state.isLoading = false
        state.hash = hashSum(item)
        state.hasChanges = false
        state.isLoaded = true
      },
    )

    builder.addCase(renameSmartSegment.pending.type, (state) => {
      state.isLoading = true
      state.isLoaded = false
      state.hasChanges = false
    })

    builder.addCase(renameSmartSegment.rejected.type, (state) => {
      state.isLoading = false
    })

    builder.addCase(
      renameSmartSegment.fulfilled.type,
      (state, action: PayloadAction<SmartSegment>) => {
        if (state.item) {
          const { payload: item } = action
          const hash = hashSum(item)
          const hasChanges = state.hash !== hash

          state.hasChanges = hasChanges
          state.hash = hash
          state.item.title = action.payload.title
        }
        state.isLoading = false
        state.isLoaded = true
      },
    )

    builder.addCase(saveSmartSegmentFilter.pending.type, (state) => {
      state.isLoading = true
      state.isLoaded = false
      state.hasChanges = false
    })

    builder.addCase(saveSmartSegmentFilter.rejected.type, (state) => {
      state.isLoading = false
    })

    builder.addCase(
      saveSmartSegmentFilter.fulfilled.type,
      (state, action: PayloadAction<SmartSegment>) => {
        const { payload: item } = action
        const hash = hashSum(item)
        const hasChanges = state.hash !== hash

        state.hasChanges = hasChanges
        state.hash = hash
        state.item = item
        state.isLoaded = true
        state.isLoading = false
      },
    )

    builder.addCase(updateSmartSegmentDescription.pending.type, (state) => {
      state.isLoading = true
      state.isLoaded = false
    })

    builder.addCase(updateSmartSegmentDescription.rejected.type, (state) => {
      state.isLoading = false
    })

    builder.addCase(
      updateSmartSegmentDescription.fulfilled.type,
      (state, action: PayloadAction<SmartSegment>) => {
        if (state.item) {
          const { payload: item } = action
          const hash = hashSum(item)
          const hasChanges = state.hash !== hash

          state.hasChanges = hasChanges
          state.hash = hash
          state.item.description = action.payload.description
        }
        state.isLoaded = true
        state.isLoading = false
      },
    )
  },
})

export const currentSmartSegmentActions = {
  ...currentSegmentSlice.actions,
  fetch: fetchSmartSegment,
  create: createSmartSegment,
  rename: renameSmartSegment,
  save: saveSmartSegmentFilter,
  updateDescription: updateSmartSegmentDescription,
  getUsesList,
}

export const currentSmartSegmentReducer = currentSegmentSlice.reducer
